import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import { registerLayer } from "@/ui/layers";
import { lockBodyScroll } from "@/ui/scrollLock";

const focusable =
  "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";
export function useModalFocus(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  dismiss: () => void,
  kind: "dialog" | "drawer" = "dialog",
) {
  const close = useRef(dismiss);
  useEffect(() => {
    close.current = dismiss;
  }, [dismiss]);
  useEffect(() => {
    const element = ref.current;
    if (!open || !element) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlockScroll = lockBodyScroll();
    const available = (node: HTMLElement) => {
      if (node.tabIndex < 0 || node.matches(":disabled")) return false;
      for (let parent: HTMLElement | null = node; parent; parent = parent.parentElement) {
        const style = getComputedStyle(parent);
        if (
          parent.hidden ||
          parent.inert ||
          parent.getAttribute("aria-hidden") === "true" ||
          style.display === "none" ||
          style.visibility === "hidden" ||
          style.visibility === "collapse"
        )
          return false;
        if (parent === element) break;
      }
      return true;
    };
    const controls = () => [...element.querySelectorAll<HTMLElement>(focusable)].filter(available);
    const preferred = element.querySelector<HTMLElement>("[data-autofocus]");
    const initial =
      (preferred && available(preferred) ? preferred : null) ?? controls()[0] ?? element;
    initial.focus();
    const remove = registerLayer(kind, () => close.current());
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = controls();
      const first = items[0],
        last = items.at(-1);
      if (!first) {
        event.preventDefault();
        element.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || !element.contains(document.activeElement))
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !element.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    element.addEventListener("keydown", trap);
    return () => {
      remove();
      element.removeEventListener("keydown", trap);
      unlockScroll();
      if (
        previous?.isConnected &&
        (document.activeElement === document.body || element.contains(document.activeElement))
      )
        previous.focus();
    };
  }, [ref, open, kind]);
}
